import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, Wand2, CheckCircle } from 'lucide-react';
import { authService } from '../../services/auth';

interface LoginFormProps {
  onSwitchToRegister: () => void;
  onForgotPassword: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToRegister, onForgotPassword }) => {
  const { login, isLoading, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email is invalid';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    clearError();
    
    try {
      await login(email, password);
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (validationErrors.email) {
      setValidationErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (validationErrors.password) {
      setValidationErrors(prev => ({ ...prev, password: '' }));
    }
  };

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setValidationErrors({ email: 'Email is required' });
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setValidationErrors({ email: 'Email is invalid' });
      return;
    }

    setMagicLinkLoading(true);
    setMagicLinkError(null);

    try {
      await authService.sendMagicLink(email);
      setMagicLinkSent(true);
    } catch (err: any) {
      setMagicLinkError(err?.message || 'Failed to send magic link. Please try again.');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  // Magic link sent success state
  if (magicLinkSent) {
    return (
      <Card className="w-full max-w-md auth-card">
        <CardHeader className="text-center space-y-2 pb-6">
          <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <CardTitle className="text-2xl font-bold">Check your email</CardTitle>
          <CardDescription className="text-base text-muted-foreground">
            We've sent a login link to <strong>{email}</strong>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            Click the link in the email to sign in. If you don't see it, check your spam folder.
          </p>
        </CardContent>

        <CardFooter className="flex flex-col space-y-4 pt-6">
          <Button
            variant="outline"
            className="w-full h-12"
            onClick={() => {
              setMagicLinkSent(false);
              setShowMagicLink(false);
              setEmail('');
            }}
          >
            Back to login
          </Button>
        </CardFooter>
      </Card>
    );
  }

  // Magic link form
  if (showMagicLink) {
    return (
      <Card className="w-full max-w-md auth-card">
        <CardHeader className="text-center space-y-2 pb-6">
          <CardTitle className="text-2xl font-bold">Passwordless login</CardTitle>
          <CardDescription className="text-base text-muted-foreground">
            Enter your email and we'll send you a magic link to sign in
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleMagicLinkSubmit} className="space-y-6">
          <CardContent className="space-y-6">
            {magicLinkError && (
              <Alert variant="destructive" className="animate-bounce-in">
                <AlertDescription>{magicLinkError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-3">
              <Label htmlFor="magic-email" className="text-sm font-medium text-foreground">Email address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  id="magic-email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={handleEmailChange}
                  className={`pl-10 h-12 input-enhanced ${
                    validationErrors.email
                      ? 'border-destructive focus:border-destructive ring-destructive/20'
                      : ''
                  }`}
                  autoFocus
                />
              </div>
              {validationErrors.email && (
                <p className="text-sm text-destructive animate-slide-down flex items-center">
                  {validationErrors.email}
                </p>
              )}
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-6 pt-6">
            <Button
              type="submit"
              className="w-full h-12 btn-primary text-base font-medium group relative overflow-hidden !text-white"
              disabled={magicLinkLoading}
            >
              {magicLinkLoading ? (
                <div className="flex items-center">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <Wand2 className="w-4 h-4 mr-2" />
                  Send magic link
                </div>
              )}
            </Button>

            <div className="text-center text-sm">
              <button
                type="button"
                onClick={() => {
                  setShowMagicLink(false);
                  setMagicLinkError(null);
                }}
                className="text-primary hover:text-primary-hover font-medium transition-colors hover:underline"
              >
                Back to password login
              </button>
            </div>
          </CardFooter>
        </form>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md auth-card">
      <CardHeader className="text-center space-y-2 pb-6">
        <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
        <CardDescription className="text-base text-muted-foreground">
          Sign in to your TinyScale account to continue
        </CardDescription>
      </CardHeader>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label htmlFor="email" className="text-sm font-medium text-foreground">Email address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={handleEmailChange}
                className={`pl-10 h-12 input-enhanced ${
                  validationErrors.email 
                    ? 'border-destructive focus:border-destructive ring-destructive/20' 
                    : ''
                }`}
              />
            </div>
            {validationErrors.email && (
              <p className="text-sm text-destructive animate-slide-down flex items-center">
                {validationErrors.email}
              </p>
            )}
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-sm font-medium text-foreground">Password</Label>
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-sm text-primary hover:text-primary-hover transition-colors"
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={handlePasswordChange}
                className={`pl-10 pr-10 h-12 input-enhanced ${
                  validationErrors.password 
                    ? 'border-destructive focus:border-destructive ring-destructive/20' 
                    : ''
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {validationErrors.password && (
              <p className="text-sm text-destructive animate-slide-down flex items-center">
                {validationErrors.password}
              </p>
            )}
          </div>
        </CardContent>
        
        <CardFooter className="flex flex-col space-y-4 pt-6">
          <Button
            type="submit"
            className="w-full h-12 btn-primary text-base font-medium group relative overflow-hidden !text-white"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Signing in...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                Sign in
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            )}
          </Button>

          <div className="relative w-full">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full h-12 text-base font-medium"
            onClick={() => setShowMagicLink(true)}
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Sign in with magic link
          </Button>

          <div className="text-center text-sm pt-2">
            <span className="text-muted-foreground">Don't have an account? </span>
            <button
              type="button"
              onClick={onSwitchToRegister}
              className="text-primary hover:text-primary-hover font-medium transition-colors hover:underline"
            >
              Create account
            </button>
          </div>
        </CardFooter>
      </form>
    </Card>
  );
};